<template>
    <main>
        <div class="graphics-box graphics-box--01 rellax" data-rellax-speed="2"></div>
        <div class="table_intro" >
            <div class="toggle-container" :style="{margin: location == 'auth' ? '0 auto 20px auto' : '30px auto 48px auto'}">
                <button :class="{ active: billingCycle === 'yearly' }" @click="setBillingCycle('yearly')">
                    <div class="toggle-discount">
                        <span>{{ $t('stripe.yearly_payment') }}</span>
                        <span class="discount-box">{{ $t('stripe.yearly_discount') }}</span>
                    </div>
                </button>
                <button :class="{ active: billingCycle === 'monthly' }" @click="setBillingCycle('monthly')">
                    {{ $t('stripe.monthly_payment') }}
                </button>
            </div>

            <div v-if="location == 'auth'" class="text-16px font-600 text-center text-[#F93C34] mb-40px">
                {{ $t('stripe.trial_info') }}
            </div>

            <div class="pricing-layout">
                <div class="features-column" :style="featuresColumnStyle">
                    <div v-for="(feature, index) in features" :key="index"
                        :class="['feature-item', { disabled: Boolean(feature.disabled) }]">
                        {{ feature.name }}
                    </div>
                    <div class="feature-item price-per-month">{{ $t('pt_price_per_month') }}</div>
                </div>
                <div class="pricing-container">
                    <div v-for="(plan, index) in pricingPlans" :key="index"
                        :class="['pricing-card', { popular: Boolean(plan.top) }]" :style="pricingCardStyle">
                        <div>
                            <div v-if="Boolean(plan.top)" class="top_badge">TOP</div>
                            <h2>{{ plan.name.toUpperCase() }}</h2>
                            <div class="features-container">
                                <div v-for="(feature, fIndex) in filteredFeatures(plan)" :key="fIndex"
                                    class="feature-status">
                                    <span v-if="plan.features[fIndex] === true">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                            fill="currentColor" class="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                            <path
                                                d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                        </svg>
                                    </span>
                                    <span v-else-if="plan.features[fIndex]">{{
                                        plan.features[fIndex]
                                        }}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div :key="billingCycle" class="price" flex gap-10px justify-center>
                                <div>
                                    <span>
                                        {{
                                            billingCycle === 'yearly' ? Math.floor(plan.yearlyPrice) :
                                                Math.floor(plan.monthlyPrice)
                                        }}
                                    </span>
                                    <sup>
                                        {{
                                            ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice) %
                                                1).toFixed(2).substring(2)
                                                !==
                                                '00'
                                                ? ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice) %
                                                    1).toFixed(2).substring(2)
                                                : ''
                                        }}
                                    </sup>{{ $currency ? $currency.symbol : '€' }}
                                </div>
                            </div>
                            <a class="table_plan_btn" @click="showInvoiceDetails(plan)">
                                <div v-if="plan.buttonArrow && (!isPlanActive(plan) || location == 'auth')" class="arrow_right">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22"
                                        fill="none">
                                        <path
                                            d="M19.25 10.9999H2.75M12.8333 4.58325L19.25 10.9999L12.8333 4.58325ZM19.25 10.9999L12.8333 17.4166L19.25 10.9999Z"
                                            stroke="#F93C34" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </div>
                                <span v-if="!isPlanActive(plan)">
                                    {{ plan.buttonText }}
                                </span>
                                <span v-else>
                                   {{ location == 'auth' ? $t('stripe.try_free') : $t('stripe.active_plan') }}
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mobile-pricing-layout">
                <div class="mobile-pricing-tabs">
                    <div v-for="(plan, index) in pricingPlans" :key="index"
                        :class="['tab-item', { 'active-tab': activeIndex === index }]" @click="goToSlide(index)">
                        {{ plan.name }}
                    </div>
                </div>

                <div class="swiper-container pricing-carousel-wrapper">
                    <div class="swiper-wrapper">
                        <div class="swiper-slide pricing-card" v-for="(plan, index) in pricingPlansMobile" :key="index">
                            <div>
                                <h2>{{ plan.name.toUpperCase() }}</h2>
                                <div :key="billingCycle" class="price" flex gap-10px justify-center style="margin-top: 0px;">
                                    <div>
                                        <span>
                                            {{
                                                billingCycle === 'yearly' ? Math.floor(plan.yearlyPrice) :
                                                    Math.floor(plan.monthlyPrice)
                                            }}
                                        </span>
                                        <sup>
                                            {{
                                                ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice) %
                                                    1).toFixed(2).substring(2)
                                                    !==
                                                    '00'
                                                    ? ((billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice) %
                                                        1).toFixed(2).substring(2)
                                                    : ''
                                            }}
                                        </sup>{{ $currency ? $currency.symbol : '€' }}
                                    </div>
                                </div>
                                <div class="mobile-features-container">
                                    <div v-for="(feature, fIndex) in filteredFeatures(plan)" :key="fIndex" :class="[
                                        'mobile-feature-status',
                                        {
                                            disabled: Boolean(feature.disabled),
                                        },
                                    ]">
                                        <span v-if="plan.features[fIndex]">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                fill="currentColor" class="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                                <path
                                                    d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                            </svg>
                                        </span>
                                        {{
                                            plan.features[fIndex] === true
                                                ? features[fIndex].name
                                                : null
                                        }}
                                        {{
                                            plan.features[fIndex] !== true && plan.features[fIndex]
                                                ? plan.features[fIndex]
                                                : null
                                        }}
                                    </div>
                                </div>
                            </div>
                            <div> 
                                <a class="table_plan_btn" @click="showInvoiceDetails(plan)">
                                    <div v-if="plan.buttonArrow" class="arrow_right">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                                            viewBox="0 0 22 22" fill="none">
                                            <path
                                                d="M19.25 10.9999H2.75M12.8333 4.58325L19.25 10.9999L12.8333 4.58325ZM19.25 10.9999L12.8333 17.4166L19.25 10.9999Z"
                                                stroke="#F93C34" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </div>
                                    <span>
                                        {{ plan.buttonText }}
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="swiper-pagination"></div>
                </div>
            </div>
            <div class="table_outro">
                <h2 v-if="location == 'auth'" class="text-16px font-700 text-center">{{ $t('stripe.upsell_msg', { price: outroPrice() }) }}</h2>
                <button @click="goToCompareFeatures" class="link" id="compare-features-button">
                    <span>{{ $t('stripe.compare_plans') }}</span>
                    <div class="arrow_right">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <path
                                d="M19.25 10.9999H2.75M12.8333 4.58325L19.25 10.9999L12.8333 4.58325ZM19.25 10.9999L12.8333 17.4166L19.25 10.9999Z"
                                stroke="#F93C34" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                </button>
            </div>
        </div>
        <InvoiceDetails 
            :openModal="openInvoiceDetailsModal" 
            :user_invoice_details="user_invoice_details" 
            @closeModal="openInvoiceDetailsModal = false" 
            @save="continueToPayment"/>
    </main>
</template>

<script>
import { EventBus } from "@/eventBus";
import axios from "axios";
import Swiper from 'swiper';
import 'swiper/css';
import InvoiceDetails from './InvoiceDetails.vue';
export default {
    components: {
        InvoiceDetails,
    },
    props: {
        activePlan: {
            type: String,
            default: null,
        },
        paymentType: {
            type: String,
            default: "year",
        },
        is_paid: {
            type: Number,
            default: 0,
        },
        user_id: {
            type: Number,
            default: 0,
        },
        is_logged_in: {
            type: Number,
            default: 0,
        },
        location: {
            type: String,
            default: "admin",
        },
        subscription_status: {
            type: String,
        },
        user_invoice_details: {
            type: Object,
            default: null,
        },
        is_invoice_details_filled: {
            type: Boolean,
            default: false,
        },
        plan: {
            type: Object,
            default: null,
        },
    },
    data() {
        const __P = (typeof window !== 'undefined' && window.__PRICING__) || {};
        const isCzk = typeof window !== 'undefined' && window.__CURRENCY__ && window.__CURRENCY__.code === 'CZK';
        const p = (key, period, eurFallback, czkFallback) => {
            if (__P[key] && __P[key][period] != null) return Number(__P[key][period]);
            return isCzk ? czkFallback : eurFallback;
        };
        return {
            billingCycle: "yearly",
            activeIndex: 0,
            pricingSwiper: null,
            showTooltip: false,
            openInvoiceDetailsModal: false,
            selectedPlan: null,
            invoice_details: null,
            cachedPrices: {
                profi: { month: p('profi', 'month', 9.99, 240), year: p('profi', 'year', 7.99, 192) },
                small: { month: p('small', 'month', 19.99, 485), year: p('small', 'year', 15.99, 388) },
                large: { month: p('large', 'month', 29.95, 725), year: p('large', 'year', 23.96, 580) },
            },
        };
    },
    created() {
        EventBus.$on("billingCycleChanged", this.updateBillingCycle);
    },
    beforeDestroy() {
        EventBus.$off("billingCycleChanged", this.updateBillingCycle);
    },
    mounted() {
        // check if there is a plan in session storage
        if (document.querySelector(".mobile-pricing-layout")) {
            this.pricingSwiper = new Swiper(".pricing-carousel-wrapper", {
                speed: 400,
                loop: false,
                slidesPerView: 1.2,
                spaceBetween: 15,
                centeredSlides: true,
                watchSlidesProgress: true,
                pagination: {
                    el: ".swiper-pagination",
                    type: "bullets",
                },
                on: {
                    slideChange: () => {
                        this.activeIndex = this.pricingSwiper.activeIndex;
                    },
                },
            });
        }
    },
    watch: {
        plan: {
            handler(newVal) {
                if(newVal){
                    this.selectedPlan = newVal.plan;
                    this.billingCycle = newVal.billingCycle;
                    this.continueToPayment(this.user_invoice_details);
                }
            },
        },
    },
    methods: {
        // Denná cena v upsell texte. EUR: pôvodná marketingová hodnota.
        // CZK: odvodená z najlacnejšieho plateného plánu (profi, ročná platba).
        outroPrice() {
            const isCzk = typeof window !== 'undefined' && window.__CURRENCY__ && window.__CURRENCY__.code === 'CZK';
            if (!isCzk) {
                return '0,26€';
            }
            const P = (typeof window !== 'undefined' && window.__PRICING__) || {};
            const profiYear = (P.profi && P.profi.year != null) ? Number(P.profi.year) : 192;
            const daily = profiYear * 12 / 365;
            return daily.toFixed(2).replace('.', ',') + ' Kč';
        },
        cancelSubscription() {
            if(this.subscription_status == this.$t('stripe.sub_status_cancelled')){
                this.$toast.error('Tvoje predplatné je už zrušené');
                return;
            }
            axios.post("/api/stripe/customer-portal-cancel", {
                user_id: this.user_id,
            })
            .then((response) => {
                window.location.href = response.data.url;
            });
        },
        changePlan(priceId) {
            axios.post("/api/stripe/customer-portal-switch-plan", {
                price_id: priceId,
                user_id: this.user_id,
            })
            .then((response) => {
                window.location.href = response.data.url;
            });
        },
        goToCompareFeatures() {
            // this dont scroll to the compare features list
            window.open("/cennik#compare-features-list", "_blank");
        },
        showInvoiceDetails(plan) {
            this.selectedPlan = plan;
            // if location is 'auth' and user is not logged in
            if(this.location == 'auth' && this.is_logged_in == 1){
                this.createCheckoutSession();
                return;
            }
            // if(this.is_invoice_details_filled){
            //     this.openInvoiceDetailsModal = false;
            //     this.invoice_details = this.user_invoice_details;
            //     this.createCheckoutSession();
            //     return;
            // }
            this.openInvoiceDetailsModal = true;
        },
        continueToPayment(invoice_details) {
            this.openInvoiceDetailsModal = false;
            this.invoice_details = invoice_details;
            this.createCheckoutSession();
        },
        createCheckoutSession() {
            let plan = this.selectedPlan;
            if (this.is_logged_in != 0) {
                let priceId = this.billingCycle == 'yearly' ? plan.yearID : plan.monthID;
                if(this.is_paid == 1){
                    if(priceId == 10){
                        this.cancelSubscription();
                    }else{
                        this.changePlan(priceId);
                    }
                    return;
                }
                if (priceId == 10) {
                    if(this.location == 'auth'){
                        this.$emit('free-plan-selected');
                    }else{
                        window.location.href = "/admin";
                    }
                    // free plán nejde cez Stripe checkout — bez return by request nižšie
                    // padol na 500 (prázdny line_items price)
                    return;
                }
                if (this.location == 'auth') {
                    axios
                    .post("/api/stripe/create-checkout-session-without-payment", {
                        discount: sessionStorage.getItem('digiuni_discount') && this.billingCycle == 'yearly',
                        price_id: priceId,
                        user_id: this.user_id,
                    })
                    .then((response) => {
                        window.location.href = response.data.url;
                    });
                }else{
                    axios
                    .post("/api/stripe/create-checkout-session", {
                        price_id: priceId,
                        user_id: this.user_id,
                        location: this.location,
                        invoice_details: this.invoice_details,
                    })
                    .then((response) => {
                        window.location.href = response.data.url;
                    });
                }
            }
        },
        goToSlide(index) {
            if (this.pricingSwiper) {
                this.pricingSwiper.slideTo(index);
            }
            this.activeIndex = index;
        },
        setBillingCycle(cycle) {
            EventBus.$emit("billingCycleChanged", cycle);
        },
        updateBillingCycle(cycle) {
            this.billingCycle = cycle;
        },
    },
    computed: {
        pricingPlans() {
            return [
                {
                    name: this.$t('stripe.plan_free'),
                    top: false,
                    monthID: 10,
                    yearID: 10,
                    monthlyPrice: 0,
                    yearlyPrice: 0,
                    yearlyPriceDiscount: 0,
                    features: ["max 3", "max 3", "", "", "", "", "", "", "", "", "", ""],
                    buttonText: this.location == 'auth' ? this.$t('stripe.try_free') : this.$t('stripe.choose_plan'),
                    buttonDiscount: this.location == 'auth' ? this.$t('stripe.try_free') : this.$t('stripe.your_plan'),
                    buttonArrow: true,
                },
                {
                    name: this.$t('stripe.plan_profi'),
                    top: false,
                    monthID: 14,
                    yearID: 13,
                    monthlyPrice: this.cachedPrices.profi.month,
                    yearlyPrice: this.cachedPrices.profi.year,
                    yearlyPriceDiscount: this.cachedPrices.profi.year / 2,
                    features: [true, true, true, true, true, true, "", "", "", "", "", ""],
                    buttonText: this.location == 'auth' ? this.$t('stripe.try_free') : this.$t('stripe.choose_plan'),
                    buttonDiscount: this.$t('stripe.prepay_discount'),
                    buttonArrow: true,
                },
                {
                    name: this.$t('stripe.plan_small_team'),
                    top: true,
                    monthID: 16,
                    yearID: 15,
                    monthlyPrice: this.cachedPrices.small.month,
                    yearlyPrice: this.cachedPrices.small.year,
                    yearlyPriceDiscount: this.cachedPrices.small.year / 2,
                    features: [
                        true,
                        true,
                        true,
                        true,
                        true,
                        true,
                        this.$t('stripe.up_to') + ' 5',
                        true,
                        false,
                        true,
                        true,
                        true,
                        true,
                    ],
                    buttonText: this.location == 'auth' ? this.$t('stripe.try_free') : this.$t('stripe.choose_plan'),
                    buttonDiscount: this.$t('stripe.prepay_discount'),
                    buttonArrow: true,
                },
                {
                    name: this.$t('stripe.plan_big_team'),
                    top: false,
                    monthID: 18,
                    yearID: 17,
                    monthlyPrice: this.cachedPrices.large.month,
                    yearlyPrice: this.cachedPrices.large.year,
                    yearlyPriceDiscount: this.cachedPrices.large.year / 2,
                    features: [
                        true,
                        true,
                        true,
                        true,
                        true,
                        this.$t('stripe.up_to') + ' 20',
                        true,
                        true,
                        true,
                        true,
                        true,
                    ],
                    buttonText: this.location == 'auth' ? this.$t('stripe.try_free') : this.$t('stripe.choose_plan'),
                    buttonDiscount: this.$t('stripe.prepay_discount'),
                    buttonArrow: true,
                },
            ];
        },
        pricingPlansMobile() {
            return this.pricingPlans.map(plan => ({
                ...plan,
                features: plan.features.map((f, i) => {
                    if (i === 0 && f === 'max 3') return this.$t('stripe.plan_free_clients');
                    if (i === 1 && f === 'max 3') return this.$t('stripe.plan_free_offers');
                    if (plan.name === this.$t('stripe.plan_small_team') && i === 6 && f) return this.$t('stripe.plan_small_team_acc');
                    if (plan.name === this.$t('stripe.plan_big_team') && i === 5 && f) return this.$t('stripe.plan_big_team_acc');
                    return f;
                }),
            }));
        },
        features() {
            return [
                { name: this.$t('stripe.feat_unlimited_clients') },
                { name: this.$t('stripe.feat_unlimited_offers2') },
                { name: this.$t('stripe.feat_hide_logo') },
                { name: this.$t('stripe.feat_custom_colors') },
                { name: this.$t('stripe.feat_success_stats') },
                { name: this.$t('stripe.feat_ai_descriptions') },
                { name: this.$t('stripe.feat_team_accounts') },
                { name: this.$t('stripe.feat_seller_stats') },
                { name: this.$t('stripe.feat_priority_support') },
                { name: this.$t('stripe.feat_ai_offers'), disabled: true },
                {
                    name: "⁠Varianty v cenovej ponuke (pripravujeme)",
                    disabled: true,
                },
                { name: this.$t('stripe.feat_margin'), disabled: true },
                { disabled: true },
            ];
        },

        featuresColumnStyle() {
            return {
                width: this.location == 'auth' ? '350px' : '300px'
            };
        },
        pricingCardStyle() {
            return {
                width: this.location == 'auth' ? '230px' : '180px'
            };
        },
        filteredFeatures() {
            return (plan) => {
                return this.features.filter((feature, index) => {
                    return Boolean(
                        plan.features[index] === "visible" ||
                        plan.features[index]?.length > 0 ||
                        plan.features[index] === true
                    );
                });
            };
        },
        isPlanActive() {
            return (plan) => {
                return plan.name === this.activePlan && this.paymentType === this.billingCycle;
            }
        },
    },
};
</script>

<style scoped lang="scss">
*{
    font-family: Montserrat, sans-serif;
}
@media (max-width: 800px) {
    .after_success_payment_message {
        padding: 13px !important;
        height: auto !important;

        i {
            width: 30px !important;
        }
    }
}

.table_intro .have_to_be_logged_in {
    width: fit-content;
    margin: auto;
    margin-top: 30px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    background-color: #EFF6FF;
    padding: 15px 30px;
    border-radius: 6px;

    p,
    a {
        color: #1E40AF;
        font-weight: 400;
        font-size: 14px;
    }

    a {
        text-decoration: underline;
    }
}

.swiper-container {
    overflow-x: hidden;
    padding-bottom: 30px;
}

.toggle-container {
    display: flex;
    justify-content: center;
    background-color: white;
    width: fit-content;
    margin: auto;
    border-radius: 12px;
    border: 1px solid #e9e9e9;
    margin-bottom: 48px;
    margin-top: 30px;
    opacity: 1;
}

.toggle-container .toggle-discount {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.toggle-container .discount-box {
    background-color: #34d399;
    border-radius: 30px;
    padding: 4px 5px 2px 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 800;
    line-height: 100%;
    color: white;
}

.toggle-container button {
    padding: 8px 33px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #212541;
    border-radius: 12px;
    transition: 0.3s;
    font-weight: 500;
}

.toggle-container button.active {
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    color: white;
    transform: scale(1.03);
}

.pricing-layout {
    display: flex;
    width: fit-content;
    justify-content: space-between;
    align-items: flex-start;
    background-color: white;
    opacity: 1;
    padding: 0 0 0 24px;
    position: relative;
    border-radius: 10px;
    border: 1px solid #E5E7EB;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
}

.features-column {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-right: 20px;
    margin-top: 45px;
    width: 220px;
}

.feature-item {
    padding: 12.5px 0;
    color: #6b6f8c;
    white-space: nowrap;
    font-size: 14px;
    height: 46px;
}

.feature-item.disabled {
    color: #bababa;
}

.feature-item.price-per-month {
    padding: 10px 0;
    margin-top: 27px;
    white-space: nowrap;
    color: #404355;
    font-weight: 800;
}

.pricing-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    height: 100%;
}
.pricing-container .pricing-card:first-child {
    border-right: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    border-radius: 0;
}
.pricing-card {
    width: 160px;
    padding: 15px;
    border-radius: 10px;
    text-align: center;
    transition: transform 0.3s, box-shadow 0.3s;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: white;
}

.pricing-card h2 {
    color: #f93c34;
    font-weight: 800;
    font-size: 16px;
    margin-top: 10px;
    margin-bottom: 10px;
    white-space: nowrap;
}

.pricing-card .top_badge {
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    color: white;
    border-radius: 17px;
    padding: 0 6px;
    padding-top: 5px;
    padding-bottom: 3px;
    position: absolute;
    top: 0;
    font-size: 14px;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 14px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 22px;
}

.popular {
    transform: translateY(-15px);
    height: 102.5%;
    min-width: 160px;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    background: white;
}

.popular a.table_plan_btn {
    color: white;
}

a.table_plan_btn {
    .tooltip {
        pointer-events: none;

        @media (max-width: 600px) {
            left: 41% !important;
        }
    }
}

a.table_plan_btn:hover {
    .tooltip {
        opacity: 1 !important;
    }
}

.popular a.table_plan_btn::before {
    background: transparent;
}

.table_intro {
    text-align: center;
    padding-bottom: 50px;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
    opacity: 1;
}

.table_intro .plan_table_text_title {
    width: 100%;
    max-width: none;
    font-family: "Fontfabric";
    font-size: 54px;
    font-style: normal;
    font-weight: 800;
    line-height: 65px;
    background: linear-gradient(90deg,
            #f93c34 30%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 19px;
}

.discount_text {
    background: linear-gradient(90deg,
            #f93c34 30%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    color: white;
    border-radius: 17px;
    -webkit-text-fill-color: white;
    padding: 5px;
    padding-top: 8px;
    font-size: 50px;

    @media (max-width: 1000px) {
        white-space: nowrap;
        font-size: 30px;
    }
}

.table_intro .plan_table_text_subtitle {
    font-family: "Fontfabric";
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
    color: #6b6f8c;
}

.table_intro .plan_table_text_subtitle .highlight_text {
    font-weight: 600;
    color: #f93c34;
}

.plan_table_text_check_wrapper {
    display: flex;
    gap: 16px;
    justify-content: center;
    margin-top: 26px;
}

.table_intro .plan_table_text_check_wrapper .plan_table_text_check {
    font-weight: 500;
    font-size: 14px;
    color: #6b6f8c;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
}

.table_outro {
    text-align: center;
    padding-top: 20px;
}

.table_outro .title {
    font-size: 22px;
    font-weight: 800;
    color: #212541;
}

.table_outro .link {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding-top: 14px;
    margin: auto;
    position: relative;
    text-decoration: none;
}

.table_outro .link::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 88%;
    height: 2px;
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
}

.price span.discount-price {
    font-size: 16px;
    text-decoration: line-through;
    -webkit-text-fill-color: #6B6F8C;
    background: transparent;
    text-decoration-color: #6B6F8C;

}

.price sup.discount-price {
    text-decoration: line-through;
    font-size: 16px;
    -webkit-text-fill-color: #6B6F8C;
    background: transparent;
    text-decoration-color: #6B6F8C;
}

.table_outro .link span {
    font-size: 20px;
    font-weight: 800;
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.table_outro .arrow_right {
    transition: all 0.2s ease-in-out;
    width: 23px;
    height: 23px;
    flex-shrink: 0;
    border-radius: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #faefef;
    transition: all 0.2s ease-in-out;
    padding: 3px;
}

@media only screen and (max-width: 1000px) {
    .table_intro .plan_table_text_title {
        font-size: 28px;
        line-height: normal;
    }

    .table_intro .plan_table_text_subtitle {
        font-size: 16px;
        line-height: normal;
    }

    .plan_table_text_check_wrapper {
        flex-direction: column;
        gap: 0;
    }
}

.price {
    font-family: "Fontfabric";
    font-size: 30px;
    font-style: normal;
    font-weight: 800;
    margin: 0 auto;
    margin-top: 15px;
    margin-bottom: 10px;
    background: linear-gradient(90deg,
            #f93c34 20%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.price sup {
    font-size: 0.6em;
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}
.pricing-card.popular .features-container {
    border: 3px solid #F93C34;
}
.pricing-card .features-container {
    border-radius: 12px;
    overflow: hidden;
    background-color: #fff6f6;
}

.feature-status {
    padding: 12.5px 0;
    color: #f93c34;
    display: flex;
    align-items: center;
    min-height: 46px;
    justify-content: center;
    font-size: 14px;
    height: 46px;
}

.feature-status.feature-status-visible {
    height: 46px;
}

.mobile-feature-status {
    color: #6b6f8c;
    padding: 10px 0;
    width: fit-content;
    display: flex;
    gap: 6px;
    text-align: left;
}

.mobile-feature-status.disabled {
    color: #bababa;
}

.mobile-feature-status svg {
    color: #f93c34;
    height: 20px;
    width: 20px;
}

a.table_plan_btn .arrow_right {
    transition: all 0.2s ease-in-out;
    width: 23px;
    height: 23px;
    flex-shrink: 0;
    border-radius: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #faefef;
    transition: all 0.2s ease-in-out;
    padding: 4px;
    z-index: 10;
}

a.table_plan_btn {
    cursor: pointer;
    display: inline-flex;
    height: 62px;
    padding: 13px 22px 13px 22px;
    font-size: 12px;
    font-weight: 800;
    max-width: 158px;
    justify-content: center;
    align-items: center;
    gap: 7px;
    width: fit-content;
    flex-shrink: 0;
    border-radius: 58px;
    font-weight: 800;
    text-decoration: none;
    color: #ce2e6c;
    position: relative;
    text-transform: uppercase;
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
}

a.table_plan_btn::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    background: white;
    border-radius: 55px;
    z-index: 0;
}

a.table_plan_btn span {
    position: relative;
    z-index: 1;
}

a.table_plan_btn:hover {
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    color: white;
}

a.table_plan_btn:hover .arrow_right {
    transform: translate(0) !important;
}

a.table_plan_btn:hover::before {
    background: transparent;
}

.swiper-wrapper {
    max-width: 260px;
    position: relative;
}

.swiper-pagination {
    bottom: 0 !important;
}

.mobile-pricing-layout {
    width: calc(100% + 15px);
    overflow: visible;
    height: fit-content;
    position: relative;
}

.mobile-pricing-layout .swiper-slide {
    width: 100%;
    height: auto;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    padding: 26px;
}

.mobile-pricing-layout {
    display: none;
}

@media (max-width: 1024px) {
    .mobile-pricing-layout {
        display: block;
    }

    .pricing-layout {
        display: none;
    }
}

@media (max-width: 740px) {
    a.table_plan_btn {
        width: 100%;
        max-width: none;
    }

    .toggle-container {
        margin-bottom: 21px;
        font-size: 12px;
    }

    .toggle-container button {
        font-size: 12px;
    }

    .toggle-container .discount-box {
        font-size: 8px;
    }
}

.mobile-pricing-tabs {
    display: flex;
    justify-content: center;
    border: 1px solid #e9e9e9;
    margin: 0 -15px 31px -15px;
    width: calc(100% + 15px);
}

.tab-item {
    padding: 19px 15px;
    background: white;
    cursor: pointer;
    transition: background 0.3s, color 0.3s;
    width: 100%;
    text-align: center;
    display: flex;
    justify-content: center;
    white-space: nowrap;
    font-size: 12px;
}

.tab-item.active-tab {
    background: linear-gradient(90deg,
            #f93c34 0%,
            #d73c78 47.28%,
            #c41f5f 75.15%);
    color: white;
    font-weight: bold;
}
</style>
